import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiMessageSquare, FiPlus, FiRefreshCw, FiSend } from "react-icons/fi";
import { FaTimes, FaSpinner } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { Pagination as PaginationUtils } from "../../utils/pagination.utils";
import { useAuth } from "../../provider/auth.provider";
import {
  addTicketMessage,
  createTicket,
  getMyTickets,
  getTicketById,
  getTicketMessages,
} from "../../libs/tickets";
import {
  TicketItem,
  TicketMessage,
  TicketPagination,
  TicketPriority,
} from "../../types/ticket";
import { CloseModal } from "../sites/closemodal";

type TicketDepartmentKey =
  | "hr_appointment"
  | "tool_requirement"
  | "site_missed_claim"
  | "others";

interface RaiseTicketFormData {
  subject: string;
  description: string;
  priority: TicketPriority | string;
  initial_message?: string;
  department: TicketDepartmentKey | "";
}

const TICKET_DEPARTMENTS: Array<{
  value: TicketDepartmentKey;
  label: string;
  roleId: number;
}> = [
  { value: "hr_appointment", label: "HR Appointment for Any Concern", roleId: 17 },
  { value: "tool_requirement", label: "Tool Requirement or Replace", roleId: 20 },
  { value: "site_missed_claim", label: "Site Missed in Claim", roleId: 4 },
  { value: "others", label: "Others", roleId: 17 },
];

const getDepartmentRoleId = (department: RaiseTicketFormData["department"]): number | null => {
  if (!department) return null;
  const match = TICKET_DEPARTMENTS.find((item) => item.value === department);
  return match?.roleId ?? null;
};

interface RaiseTicketOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  formData: RaiseTicketFormData;
  setFormData: React.Dispatch<React.SetStateAction<RaiseTicketFormData>>;
  creatingTicket: boolean;
  canCreateTicket: boolean;
}

const RaiseTicketOffcanvas: React.FC<RaiseTicketOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  creatingTicket,
  canCreateTicket,
}) => {
  const [showCloseModal, setShowCloseModal] = useState(false);

  const requestClose = () => {
    if (creatingTicket) return;
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !creatingTicket) {
        requestClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, creatingTicket]);

  if (!isOpen) return null;

  return (
    <>
      {/* Original overlay with lower z-index (sidebar remained highlighted)
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={!creatingTicket ? requestClose : undefined}
      />
      */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={!creatingTicket ? requestClose : undefined}
      />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Raise Ticket</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create a support ticket and track follow-up conversations.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded"
            disabled={creatingTicket}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subject: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                placeholder="Issue summary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
              <select
                value={formData.department}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    department: e.target.value as RaiseTicketFormData["department"],
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="" disabled>
                  Select department
                </option>
                {TICKET_DEPARTMENTS.map((department) => (
                  <option key={department.value} value={department.value}>
                    {department.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, priority: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe the issue in detail"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Initial Message</label>
              <textarea
                value={formData.initial_message}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, initial_message: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Optional first conversation message"
              />
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 mb-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={requestClose}
              disabled={creatingTicket}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(event) => onSubmit(event as any)}
              disabled={creatingTicket || !canCreateTicket || !formData.department}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2"
            >
              {creatingTicket ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FiPlus className="w-4 h-4" />
                  {!canCreateTicket ? "No Create Permission" : "Create Ticket"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};

const defaultPagination: TicketPagination = {
  offset: 0,
  limit: 10,
  page: 1,
  total_pages: 1,
  total_count: 0,
  has_next_page: false,
  has_prev_page: false,
  next_page: null,
  prev_page: null,
};

const RaiseTicketPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canCreateTicket = hasPermission("ticket", "create");
  const canAddMessage = hasPermission("ticket", "add_message");

  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [pagination, setPagination] = useState<TicketPagination>(defaultPagination);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);

  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    priority: "",
  });

  const [formData, setFormData] = useState<RaiseTicketFormData>({
    subject: "",
    description: "",
    priority: "medium",
    initial_message: "",
    department: "",
  });

  const [followUpMessage, setFollowUpMessage] = useState("");
  const [showCreateOffcanvas, setShowCreateOffcanvas] = useState(false);
  const isFirstFiltersEffect = useRef(true);

  const fetchTickets = async (page = pagination.page, limit = pagination.limit) => {
    setTicketsLoading(true);
    try {
      const response = await getMyTickets({
        page,
        limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
      });

      setTickets(response.tickets);
      setPagination(response.pagination || defaultPagination);

      if (response.tickets.length === 0) {
        setSelectedTicketId(null);
        setSelectedTicket(null);
        setMessages([]);
        return;
      }

      if (!selectedTicketId || !response.tickets.some((ticket) => ticket.id === selectedTicketId)) {
        setSelectedTicketId(response.tickets[0].id);
      }
    } catch (error: any) {
      console.error("Failed to fetch my tickets:", error);
      toast.error(error?.response?.data?.message || "Failed to load tickets");
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchTicketDetail = async (ticketId: number) => {
    setDetailLoading(true);
    try {
      const detail = await getTicketById(ticketId, { page: 1, limit: 25 });
      setSelectedTicket(detail.ticket);

      if (detail.messages.length > 0) {
        setMessages(detail.messages);
      } else {
        const messageResponse = await getTicketMessages(ticketId, { page: 1, limit: 50 });
        setMessages(messageResponse.messages);
      }
    } catch (error: any) {
      console.error("Failed to fetch ticket details:", error);
      toast.error(error?.response?.data?.message || "Failed to load ticket details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateTicket = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error("Subject and description are required");
      return;
    }
    if (!formData.department) {
      toast.error("Department is required");
      return;
    }

    const roleId = getDepartmentRoleId(formData.department);
    if (!roleId) {
      toast.error("Invalid department selection");
      return;
    }

    setCreatingTicket(true);
    try {
      await createTicket({
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        role_id: roleId,
        initial_message: formData.initial_message?.trim() || undefined,
      });

      setFormData({
        subject: "",
        description: "",
        priority: "medium",
        initial_message: "",
        department: "",
      });

      await fetchTickets(1, pagination.limit);
      setShowCreateOffcanvas(false);
    } catch (error) {
      console.error("Failed to create ticket:", error);
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleSendFollowUp = async () => {
    if (!selectedTicketId) return;
    if (!followUpMessage.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    setSendingMessage(true);
    try {
      await addTicketMessage(selectedTicketId, followUpMessage.trim());
      setFollowUpMessage("");
      await fetchTicketDetail(selectedTicketId);
    } catch (error) {
      console.error("Failed to send follow-up:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    fetchTickets(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply My Tickets search & filters with debouncing
  useEffect(() => {
    if (isFirstFiltersEffect.current) {
      isFirstFiltersEffect.current = false;
      return;
    }

    const handler = setTimeout(() => {
      fetchTickets(1, pagination.limit);
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.limit]);

  useEffect(() => {
    if (selectedTicketId) {
      fetchTicketDetail(selectedTicketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId]);

  const statusBadge = (status?: string) => {
    const value = (status || "").toLowerCase();
    if (value === "resolved") return "bg-green-100 text-green-700";
    if (value === "in_progress") return "bg-blue-100 text-blue-700";
    if (value === "closed") return "bg-gray-100 text-gray-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const priorityBadge = (priority?: string) => {
    const value = (priority || "").toLowerCase();
    if (value === "critical") return "bg-red-100 text-red-700";
    if (value === "high") return "bg-orange-100 text-orange-700";
    if (value === "medium") return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-700";
  };

  const orderedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [messages]
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Raise Ticket</h1>
            <p className="text-sm text-gray-600 mt-1">
              Create a support ticket and track follow-up conversations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCreateTicket && (
              <button
                type="button"
                onClick={() => setShowCreateOffcanvas(true)}
                className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 flex items-center gap-2"
              >
                <FiPlus className="w-4 h-4" />
                Create Ticket
              </button>
            )}
            <button
              onClick={() => fetchTickets(pagination.page, pagination.limit)}
              className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
            >
              <FiRefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/*
        <form onSubmit={handleCreateTicket} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Issue summary"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Describe the issue in detail"
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Message</label>
            <textarea
              value={formData.initial_message}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, initial_message: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Optional first conversation message"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={creatingTicket || !canCreateTicket}
              className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2"
            >
              <FiPlus className="w-4 h-4" />
              {!canCreateTicket ? "No Create Permission" : creatingTicket ? "Creating..." : "Create Ticket"}
            </button>
          </div>
        </form>
        */}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 bg-white rounded shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">My Tickets</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">All status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filters.priority}
                onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">All priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            {/*
            <button
              onClick={() => fetchTickets(1, pagination.limit)}
              className="mt-3 px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200"
            >
              Apply Filters
            </button>
            */}
          </div>

          <div className="divide-y divide-gray-200 max-h-[480px] overflow-y-auto">
            {ticketsLoading ? (
              <div className="p-6 text-sm text-gray-500">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No tickets found.</div>
            ) : (
              tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selectedTicketId === ticket.id ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {/* <p className="font-medium text-gray-900 text-sm">{ticket.subject}</p> */}
                    {/* <p className="font-medium text-gray-900 text-sm">
                      <span className="text-xs text-gray-500 mr-1">#{ticket.id}</span>
                      {ticket.subject}
                    </p> */}
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">#{ticket.id}</span>
                      <p className="font-medium text-gray-900 text-sm">{ticket.subject}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${priorityBadge(
                        ticket.priority
                      )}`}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${statusBadge(ticket.status)}`}
                    >
                      {ticket.status}
                    </span>
                    {/* <span className="text-xs text-gray-500">
                      #{ticket.id}
                      {new Date(ticket.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span> */}
                    <span className="text-xs text-gray-500">
                      {/*
                      {new Date(ticket.updated_at).toLocaleDateString()}
                      */}
                      {new Date(ticket.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          <PaginationUtils
            pagination={pagination}
            onPageChange={(page) => fetchTickets(page, pagination.limit)}
            onLimitChange={(limit) => fetchTickets(1, limit)}
          />
        </div>

        <div className="xl:col-span-3 bg-white rounded shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Follow-up</h2>
          </div>

          {!selectedTicket ? (
            <div className="p-8 text-center text-gray-500">
              <FiMessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              Select a ticket to view its conversation.
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedTicket.subject}</h3>
                    <p className="text-sm text-gray-600 mt-1">{selectedTicket.description}</p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-block text-xs font-medium px-2 py-1 rounded ${statusBadge(
                        selectedTicket.status
                      )}`}
                    >
                      {selectedTicket.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded">
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700">
                  Conversation
                </div>
                <div className="max-h-[320px] overflow-y-auto p-4 space-y-3">
                  {detailLoading ? (
                    <p className="text-sm text-gray-500">Loading messages...</p>
                  ) : orderedMessages.length === 0 ? (
                    <p className="text-sm text-gray-500">No messages yet.</p>
                  ) : (
                    orderedMessages.map((message) => (
                      <div key={message.id} className="bg-gray-50 border border-gray-200 rounded p-3">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.message}</p>
                        {/*
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(message.created_at).toLocaleString()}
                        </p>
                        */}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(message.created_at).toLocaleString("en-US", {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Follow-up Message
                </label>
                <textarea
                  value={followUpMessage}
                  onChange={(e) => setFollowUpMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Write your follow-up..."
                />
              </div>

              <button
                onClick={handleSendFollowUp}
                disabled={sendingMessage || !canAddMessage}
                className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2"
              >
                <FiSend className="w-4 h-4" />
                {!canAddMessage ? "No Message Permission" : sendingMessage ? "Sending..." : "Send Follow-up"}
              </button>
            </div>
          )}
        </div>
      </div>
      <RaiseTicketOffcanvas
        isOpen={showCreateOffcanvas}
        onClose={() => setShowCreateOffcanvas(false)}
        onSubmit={handleCreateTicket}
        formData={formData}
        setFormData={setFormData}
        creatingTicket={creatingTicket}
        canCreateTicket={canCreateTicket}
      />
    </div>
  );
};

export default RaiseTicketPage;
